import React, { ReactNode, useState, useRef, useEffect } from 'react';

interface TooltipTitleProps {
  children: ReactNode;
  className?: string;
}

const TooltipTitle: React.FC<TooltipTitleProps> = ({ 
  children, 
  className = ''
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Extrair o texto para o tooltip removendo elementos HTML
  const tooltipText = React.Children.toArray(children)
    .map(child => {
      if (typeof child === 'string') return child;
      if (typeof child === 'number') return child.toString();
      return '';
    })
    .join(' ')
    .trim();

  // Verifica se o texto está truncado
  const isTextTruncated = () => {
    const element = titleRef.current;
    if (element) {
      return element.offsetWidth < element.scrollWidth;
    }
    return false;
  };

  // Calcula a posição do tooltip
  const updateTooltipPosition = () => {
    if (!titleRef.current) return;

    const rect = titleRef.current.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    setTooltipPosition({
      top: rect.top + scrollTop - 40, // 40px acima do elemento
      left: rect.left + scrollLeft
    });
  };

  const handleMouseEnter = () => {
    if (isTextTruncated()) {
      updateTooltipPosition();
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Atualiza posição ao rolar ou redimensionar
  useEffect(() => {
    if (showTooltip) {
      const handleScroll = () => updateTooltipPosition();
      const handleResize = () => updateTooltipPosition();

      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showTooltip]);

  return (
    <>
      <h4 
        ref={titleRef}
        className={`fw-semibold title-column ${className} overflow-hidden text-ellipsis whitespace-nowrap w-full m-0`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </h4>
      {showTooltip && (
        <div
          style={{
            position: 'fixed',
            top: 35,
            left: 0,
            width: '100%',
            height: '0',
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '14px',
              maxWidth: '300px',
              wordWrap: 'break-word',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              animation: 'tooltipFadeIn 0.2s ease-out',
              transform: 'translateY(-100%)',
              whiteSpace: 'nowrap'
            }}
          >
            {tooltipText}
            <div
              style={{
                position: 'absolute',
                bottom: '-4px',
                left: '50%',
                marginLeft: '-4px',
                width: '8px',
                height: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                transform: 'rotate(45deg)'
              }}
            />
          </div>
        </div>
      )}
      <style>
        {`
          @keyframes tooltipFadeIn {
            from {
              opacity: 0;
              transform: translateY(-90%);
            }
            to {
              opacity: 1;
              transform: translateY(-100%);
            }
          }
        `}
      </style>
    </>
  );
};

export default TooltipTitle;